import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getServiceOrders } from 'app/entities/service-order/service-order.reducer';
import { getEntities as getEmployees } from 'app/entities/employee/employee.reducer';
import { WorkStatus } from 'app/shared/model/enumerations/work-status.model';
import { createEntity, getEntity, reset, updateEntity } from './work-order.reducer';

export const WorkOrderUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const serviceOrders = useAppSelector(state => state.serviceOrder.entities);
  const employees = useAppSelector(state => state.employee.entities);
  const workOrderEntity = useAppSelector(state => state.workOrder.entity);
  const loading = useAppSelector(state => state.workOrder.loading);
  const updating = useAppSelector(state => state.workOrder.updating);
  const updateSuccess = useAppSelector(state => state.workOrder.updateSuccess);
  const workStatusValues = Object.keys(WorkStatus);

  const handleClose = () => {
    navigate('/work-order');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getServiceOrders({}));
    dispatch(getEmployees({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    values.createdTimestamp = convertDateTimeToServer(values.createdTimestamp);

    const entity = {
      ...workOrderEntity,
      ...values,
      serviceOrder: serviceOrders.find(it => it.id.toString() === values.serviceOrder?.toString()),
      owner: employees.find(it => it.id.toString() === values.owner?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          createdTimestamp: displayDefaultDateTime(),
        }
      : {
          workStatus: 'NEW',
          ...workOrderEntity,
          createdTimestamp: convertDateTimeFromServer(workOrderEntity.createdTimestamp),
          serviceOrder: workOrderEntity?.serviceOrder?.id,
          owner: workOrderEntity?.owner?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="crawlerBackendApp.workOrder.home.createOrEditLabel" data-cy="WorkOrderCreateUpdateHeading">
            <Translate contentKey="crawlerBackendApp.workOrder.home.createOrEditLabel">
              Create or edit a WorkOrder
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="work-order-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.workOrder.startDate')}
                id="work-order-startDate"
                name="startDate"
                data-cy="startDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.workOrder.endDate')}
                id="work-order-endDate"
                name="endDate"
                data-cy="endDate"
                type="date"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.workOrder.workStatus')}
                id="work-order-workStatus"
                name="workStatus"
                data-cy="workStatus"
                type="select"
              >
                {workStatusValues.map(workStatus => (
                  <option value={workStatus} key={workStatus}>
                    {translate(`crawlerBackendApp.WorkStatus.${workStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.workOrder.description')}
                id="work-order-description"
                name="description"
                data-cy="description"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.workOrder.createdTimestamp')}
                id="work-order-createdTimestamp"
                name="createdTimestamp"
                data-cy="createdTimestamp"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                id="work-order-serviceOrder"
                name="serviceOrder"
                data-cy="serviceOrder"
                label={translate('crawlerBackendApp.workOrder.serviceOrder')}
                type="select"
              >
                <option value="" key="0" />
                {serviceOrders
                  ? serviceOrders.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="work-order-owner"
                name="owner"
                data-cy="owner"
                label={translate('crawlerBackendApp.workOrder.owner')}
                type="select"
              >
                <option value="" key="0" />
                {employees
                  ? employees.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button
                tag={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/work-order"
                replace
                color="info"
              >
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default WorkOrderUpdate;
